/**
 * 素材列 / 素材卡共用的零件與介面。
 *
 * 清單檢視（{@link MediaTable}）與網格檢視（{@link MediaGrid}）呈現方式不同，
 * 但可以做的事完全一樣：勾選、預覽、就地編輯語系與標籤、看引用、刪除。共用的
 * 契約放在這裡，兩個檢視才不會慢慢長出不一致的行為——例如只有其中一邊能編輯。
 */

import { useEffect } from 'react';

import { MediaTagLine, type MediaSelection } from './MediaFolderTree';
import type { MediaListItem, MediaUsageRef } from '../lib/api-client';

export interface MediaEditState {
  id: string;
  language: string;
  tags: string;
}

/** 兩個檢視共用的 props；由 MediaLibrary 一次備齊後原樣傳下去。 */
export interface MediaViewProps {
  items: MediaListItem[];
  languages: ReadonlyArray<{ value: string; label: string }>;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  editing: MediaEditState | null;
  onEditChange: (e: MediaEditState | null) => void;
  onSave: (e: MediaEditState) => void;
  saving: boolean;
  onPreview: (m: MediaListItem) => void;
  onDelete: (m: MediaListItem) => void;
  onShowUsage: (m: MediaListItem) => void;
  onFolder: (s: MediaSelection) => void;
}

/**
 * 引用標記。
 *
 * `total === 0` 時**不顯示任何東西**：148 筆裡絕大多數沒有引用，每列都掛一個
 * 「未使用」只會讓真正在用的那幾筆更難被看見。要確認能不能刪，刪除對話框會在
 * 該講的時候講。
 *
 * `blocking` 與 `total` 分開呈現：已發布的窗屏包算引用但不擋刪除（它引用的是
 * 物件內容，而軟刪除保留物件），這種情況顯示灰色的「已發布」而非橘色警示。
 */
export function MediaUsageBadge({
  item,
  onClick,
}: {
  item: MediaListItem;
  onClick: () => void;
}): JSX.Element | null {
  const { total, blocking } = item.usage;
  if (total === 0) return null;
  const warn = blocking > 0;
  return (
    <button
      onClick={onClick}
      title="查看引用位置"
      style={{
        border: `1px solid ${warn ? '#fcd34d' : '#e5e7eb'}`,
        background: warn ? '#fffbeb' : '#f9fafb',
        color: warn ? '#92400e' : '#6b7280',
        borderRadius: 10,
        padding: '1px 7px',
        fontSize: 11,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {warn ? `使用中 ${blocking}` : `已發布 ${total}`}
    </button>
  );
}

/** 引用明細；資料直接來自列表回應，不需要再打一次 API。 */
export function MediaUsageModal({
  item,
  onClose,
}: {
  item: MediaListItem;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groups = new Map<string, MediaUsageRef[]>();
  for (const r of item.usage.refs) {
    const list = groups.get(r.source_label);
    if (list) list.push(r);
    else groups.set(r.source_label, [r]);
  }

  return (
    <div onClick={onClose} style={OVERLAY}>
      <div onClick={(e) => e.stopPropagation()} style={PANEL}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <strong style={{ fontSize: 14 }}>「{item.filename}」的引用位置</strong>
          <button onClick={onClose} style={{ cursor: 'pointer' }}>
            關閉 (Esc)
          </button>
        </div>
        <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 12px' }}>
          共 {item.usage.total} 處
          {item.usage.blocking > 0
            ? `，其中 ${item.usage.blocking} 處會阻擋刪除。要刪除請先在對應頁面解除掛載。`
            : '；皆為已發布的窗屏包，不影響刪除。'}
        </p>
        {[...groups].map(([label, refs]) => (
          <div key={label} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
              {label}（{refs.length}）
            </div>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, color: '#4b5563' }}>
              {refs.map((r) => (
                <li key={`${r.source}:${r.ref_id}`}>{r.label}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

/** 就地編輯的欄位組（語系 + 標籤），清單與網格共用。 */
export function MediaEditFields({
  editing,
  languages,
  onChange,
}: {
  editing: MediaEditState;
  languages: ReadonlyArray<{ value: string; label: string }>;
  onChange: (e: MediaEditState) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
      <select
        value={editing.language}
        onChange={(e) => onChange({ ...editing, language: e.target.value })}
        style={{ padding: 4, fontSize: 12 }}
      >
        {languages.map((l) => (
          <option key={l.value} value={l.value}>
            {l.label}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={editing.tags}
        onChange={(e) => onChange({ ...editing, tags: e.target.value })}
        placeholder="標籤（逗號分隔）"
        style={{ padding: 4, flex: 1, minWidth: 0, fontSize: 12 }}
      />
    </div>
  );
}

/** 標籤 + 引用標記；兩個檢視都放在檔名下方。 */
export function MediaMetaLine({
  item,
  onFolder,
  onShowUsage,
}: {
  item: MediaListItem;
  onFolder: (s: MediaSelection) => void;
  onShowUsage: (m: MediaListItem) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <MediaTagLine tags={item.tags} onFolder={onFolder} />
      <MediaUsageBadge item={item} onClick={() => onShowUsage(item)} />
    </div>
  );
}

/** 由編輯狀態解出要送出的標籤陣列（去空白、去空項）。 */
export function parseEditTags(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

const OVERLAY: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 60,
};

const PANEL: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  padding: 16,
  minWidth: 380,
  maxWidth: '70vw',
  maxHeight: '80vh',
  overflow: 'auto',
};
